import { formatJpWithAlias } from '@/utils/partDisplay';
import StockBadge from '@/components/StockBadge';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { DialogDescription } from '@/components/ui/dialog';
import { 
  ShoppingCart, Search, Plus, Minus, Trash2, User, Building2, Percent, 
  Package, X, Check, AlertCircle, Save, Clock, Play, RefreshCw, 
  ArrowLeft, Grid3X3, Loader2, ChevronDown, Flag, UserPlus
} from 'lucide-react';
import axios from 'axios';
import { MrpEffectiveDate } from '@/components/MrpChangeBadge';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useImageCache } from '@/context/ImageCacheContext';
import ImageLightbox from './ImageLightbox';
import BangladeshExportOrderModal from './BangladeshExportOrderModal';
import QuickRegisterCustomer from './QuickRegisterCustomer';
import TransporterComboBox from './TransporterComboBox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Local storage key for draft
const CATALOGUE_ORDER_DRAFT_KEY = 'catalogue_order_draft';

// Admin mobile numbers for flag functionality
const ADMIN_MOBILE_NUMBERS = ['9999313175', '9310580743', '9810326498'];

const CatalogueOrderModal = ({ isOpen, onClose, scopeToDistributorId = null }) => {
  const { token, user } = useAuth();
  const { prefetchImages, getCachedImage, fetchImage } = useImageCache();
  
  const [step, setStep] = useState(1); // 1: Select Customer, 2: Browse Catalogue, 3: Review Cart
  
  // Customer Selection
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customerDiscount, setCustomerDiscount] = useState(0);
  const [editingDiscount, setEditingDiscount] = useState(false);
  const [newDiscount, setNewDiscount] = useState(0);
  const [specialPrices, setSpecialPrices] = useState({});
  const [inlineHandshakePrices, setInlineHandshakePrices] = useState({});
  
  // Catalogue State
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [catalogueSearch, setCatalogueSearch] = useState('');
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const ITEMS_PER_LOAD = 30;
  
  // Product images
  const [productImages, setProductImages] = useState({});
  
  // Cart
  const [cart, setCart] = useState([]);
  const [showCartDrawer, setShowCartDrawer] = useState(false);
  
  // Dispatch Details
  const [dispatchMode, setDispatchMode] = useState('transport');
  const [transporterName, setTransporterName] = useState('');
  const [dispatchRemarks, setDispatchRemarks] = useState('');
  
  // Delhi customer detection
  const isDelhiCustomer = selectedCustomer && (
    String(selectedCustomer.pincode || '').startsWith('110') ||
    String(selectedCustomer.city || '').toLowerCase().includes('delhi') ||
    String(selectedCustomer.state || '').toLowerCase().includes('delhi')
  );
  const isMasterAdmin = user?.mobile === '9999313175';
  const isDispatchLocked = isDelhiCustomer && !isMasterAdmin;
  
  // Order Submission
  const [submitting, setSubmitting] = useState(false);
  
  // Close confirmation
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  
  // Draft recovery
  const [showDraftRecovery, setShowDraftRecovery] = useState(false);
  const [savedDraft, setSavedDraft] = useState(null);
  
  // Server-side drafts
  const [serverDrafts, setServerDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  
  // Add to Cart Dialog state
  const [addToCartProduct, setAddToCartProduct] = useState(null);
  const [addToCartQuantity, setAddToCartQuantity] = useState(0);
  const [addToCartHandshakePrice, setAddToCartHandshakePrice] = useState('');
  
  // Duplicate Item Confirmation Dialog
  const [duplicateItemConfirm, setDuplicateItemConfirm] = useState(null); // {product, existingQty, newQty, handshakePrice}
  
  // Flag/Mark Product state
  const [showMarkDialog, setShowMarkDialog] = useState(false);
  const [markingProduct, setMarkingProduct] = useState(null);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [marking, setMarking] = useState(false);
  const [markedProducts, setMarkedProducts] = useState({}); // Track marked products locally
  
  // Image Lightbox state
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxAlt, setLightboxAlt] = useState('');
  
  // Bangladesh Export Modal state
  const [showBangladeshModal, setShowBangladeshModal] = useState(false);
  const [bangladeshCustomer, setBangladeshCustomer] = useState(null);
  
  // Quick Register Customer state
  const [showQuickRegister, setShowQuickRegister] = useState(false);
  
  // Check if customer is from Bangladesh (country_code: 880)
  const isBangladeshCustomer = (customer) => customer?.country_code === '880';
  // Check if user can mark products
  const isAdmin = user && ADMIN_MOBILE_NUMBERS.includes(user.mobile);
  const isCritic = user?.is_critic || false;
  const canMarkProducts = isAdmin || isCritic;
  
  // Refs
  const productContainerRef = useRef(null);
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const customerSearchIdRef = useRef(0);
  // 2026-05: catalogue product fetch race-guard. Tracks the most-recent
  // search query/skip combination so stale responses (e.g. a slow "BU"
  // response arriving after a fast "BUMP") are discarded.
  const productFetchIdRef = useRef(0);
  const debouncedSearchRef = useRef(null);
  const qtyInputRef = useRef(null);
  
  // Check if there are unsaved changes
  const hasUnsavedChanges = cart.length > 0 || selectedCustomer !== null;
  
  // Auto-set dispatch to Local for Delhi customers
  useEffect(() => {
    if (isDelhiCustomer) {
      setDispatchMode('local');
      setTransporterName('');
    }
  }, [isDelhiCustomer]);
  
  // Debounce helper
  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
      const handler = setTimeout(() => setDebouncedValue(value), delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
  };
  
  const debouncedCustomerSearch = useDebounce(customerSearch, 400);
  // 2026-05: 400ms → 250ms so catalogue search feels instant when
  // typing a JP/OE prefix. Backend warm path is ~200ms.
  const debouncedCatalogueSearch = useDebounce(catalogueSearch, 250);
  
  // Auto-save draft to localStorage - compressed format for 200+ items
  useEffect(() => {
    if (isOpen && (cart.length > 0 || selectedCustomer)) {
      try {
        const compressedCart = {};
        cart.forEach(item => {
          compressedCart[item.jp_part_no] = {
            q: item.quantity,
            p: item.price || item.mrp,
            n: item.part_name?.substring(0, 50),
            dp: item.discounted_price,
            img: item.image ? 'y' : 'n'
          };
        });
        
        const draft = {
          cc: compressedCart,
          cs: selectedCustomer ? {
            id: selectedCustomer.customer_id,
            n: selectedCustomer.name,
            cn: selectedCustomer.company_name,
            m: selectedCustomer.mobile,
            d: selectedCustomer.discount_percentage,
            st: selectedCustomer.state,
            ct: selectedCustomer.city,
            pc: selectedCustomer.pincode
          } : null,
          cd: customerDiscount,
          sp: specialPrices,
          ihp: inlineHandshakePrices,
          dm: dispatchMode,
          tn: transporterName,
          dr: dispatchRemarks,
          cat: selectedCategory,
          st: step,
          ts: Date.now()
        };
        localStorage.setItem(CATALOGUE_ORDER_DRAFT_KEY, JSON.stringify(draft));
      } catch (error) {
        console.warn('localStorage save failed:', error.message);
      }
    }
  }, [isOpen, cart, selectedCustomer, customerDiscount, specialPrices, inlineHandshakePrices, dispatchMode, transporterName, dispatchRemarks, selectedCategory, step]);
  
  // Check for saved draft when modal opens
  useEffect(() => {
    if (isOpen) {
      const savedDraftStr = localStorage.getItem(CATALOGUE_ORDER_DRAFT_KEY);
      if (savedDraftStr) {
        try {
          const draft = JSON.parse(savedDraftStr);
          const hasItems = draft.cc ? Object.keys(draft.cc).length > 0 : false;
          const hasCustomer = draft.cs;
          
          if (hasItems || hasCustomer) {
            setSavedDraft(draft);
            setShowDraftRecovery(true);
          }
        } catch (e) {
          console.error('Error parsing saved draft:', e);
          localStorage.removeItem(CATALOGUE_ORDER_DRAFT_KEY);
        }
      }
    }
  }, [isOpen]);
  
  // Restore draft function
  const restoreDraft = async () => {
    if (!savedDraft) return;
    
    if (savedDraft.cc) {
      const partNumbers = Object.keys(savedDraft.cc);
      if (partNumbers.length > 0) {
        try {
          const response = await axios.post(`${API}/products/bulk-details`, {
            part_numbers: partNumbers
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const partsMap = {};
          (response.data.products || []).forEach(p => {
            partsMap[p.jp_part_no] = p;
          });
          
          const restoredCart = partNumbers.map(partNo => {
            const compressed = savedDraft.cc[partNo];
            const fullPart = partsMap[partNo] || {};
            return {
              jp_part_no: partNo,
              part_name: fullPart.part_name || compressed.n || partNo,
              oe_part_no: fullPart.oe_part_no || '',
              mrp: fullPart.mrp || compressed.p || 0,
              price: compressed.p || fullPart.mrp || 0,
              discounted_price: compressed.dp || fullPart.discounted_price,
              quantity: compressed.q || 1,
              image: fullPart.image || null
            };
          });
          
          setCart(restoredCart);
        } catch (error) {
          console.error('Error fetching part details:', error);
          const restoredCart = partNumbers.map(partNo => {
            const compressed = savedDraft.cc[partNo];
            return {
              jp_part_no: partNo,
              part_name: compressed.n || partNo,
              mrp: compressed.p || 0,
              price: compressed.p || 0,
              discounted_price: compressed.dp,
              quantity: compressed.q || 1
            };
          });
          setCart(restoredCart);
        }
      }
      
      if (savedDraft.cs) {
        setSelectedCustomer({
          customer_id: savedDraft.cs.id,
          name: savedDraft.cs.n,
          company_name: savedDraft.cs.cn,
          mobile: savedDraft.cs.m,
          discount_percentage: savedDraft.cs.d,
          state: savedDraft.cs.st,
          city: savedDraft.cs.ct,
          pincode: savedDraft.cs.pc
        });
        setCustomerDiscount(savedDraft.cd || savedDraft.cs.d || 0);
      }
      
      setSpecialPrices(savedDraft.sp || {});
      setInlineHandshakePrices(savedDraft.ihp || {});
      setDispatchMode(savedDraft.dm || 'transport');
      setTransporterName(savedDraft.tn || '');
      setDispatchRemarks(savedDraft.dr || '');
      setSelectedCategory(savedDraft.cat || null);
      setStep(savedDraft.st >= 2 ? savedDraft.st : (savedDraft.cs ? 2 : 1));
    }
    
    toast.success('Draft restored successfully!');
    setShowDraftRecovery(false);
    setSavedDraft(null);
  };
  
  const discardDraft = () => {
    localStorage.removeItem(CATALOGUE_ORDER_DRAFT_KEY);
    setShowDraftRecovery(false);
    setSavedDraft(null);
  };
  
  // Fetch server-side drafts
  const fetchServerDrafts = useCallback(async () => {
    if (!token) return;
    setLoadingDrafts(true);
    try {
      const response = await axios.get(`${API}/drafts/my?draft_type=catalogue_order`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServerDrafts(response.data.drafts || []);
    } catch (error) {
      console.error('Error fetching drafts:', error);
    } finally {
      setLoadingDrafts(false);
    }
  }, [token]);
  
  useEffect(() => {
    if (isOpen) {
      fetchServerDrafts();
    }
  }, [isOpen, fetchServerDrafts]);
  
  // Save progress to server
  const saveProgressToServer = async () => {
    if (!selectedCustomer || cart.length === 0) {
      toast.error('Please select a customer and add items first');
      return;
    }
    
    setSavingDraft(true);
    try {
      await axios.post(`${API}/drafts/save`, {
        draft_type: 'catalogue_order',
        customer_id: selectedCustomer.customer_id,
        customer_name: selectedCustomer.name,
        company_name: selectedCustomer.company_name,
        customer_phone: selectedCustomer.mobile,
        customer_email: selectedCustomer.email,
        customer_gst: selectedCustomer.gst_number,
        customer_address: selectedCustomer.address,
        customer_state: selectedCustomer.state,
        customer_category: selectedCustomer.customer_category,
        customer_discount: customerDiscount,
        items: cart,
        special_prices: specialPrices,
        inline_handshake_prices: inlineHandshakePrices,
        dispatch_mode: dispatchMode,
        transporter_name: transporterName,
        dispatch_remarks: dispatchRemarks,
        step: step,
        total_amount: calculateTotal(),
        full_customer: selectedCustomer
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Progress saved! You can resume this order later.');
      localStorage.removeItem(CATALOGUE_ORDER_DRAFT_KEY);
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save progress');
    } finally {
      setSavingDraft(false);
    }
  };
  
  // Resume server draft
  const resumeDraft = async (draft) => {
    try {
      const customer = draft.full_customer || {
        customer_id: draft.customer_id,
        name: draft.customer_name,
        company_name: draft.company_name,
        mobile: draft.customer_phone,
        email: draft.customer_email,
        gst_number: draft.customer_gst,
        address: draft.customer_address,
        state: draft.customer_state,
        customer_category: draft.customer_category,
        discount_percentage: draft.customer_discount
      };
      
      setSelectedCustomer(customer);
      setCustomerDiscount(draft.customer_discount || customer.discount_percentage || 0);
      setSpecialPrices(draft.special_prices || {});
      setInlineHandshakePrices(draft.inline_handshake_prices || {});
      setCart(draft.items || []);
      setDispatchMode(draft.dispatch_mode || 'transport');
      setTransporterName(draft.transporter_name || '');
      setDispatchRemarks(draft.dispatch_remarks || '');
      setStep(2);
      
      toast.success('Draft loaded! Continue where you left off.');
    } catch (error) {
      console.error('Error resuming draft:', error);
      toast.error('Failed to load draft');
    }
  };
  
  const deleteServerDraft = async (draftId) => {
    try {
      await axios.delete(`${API}/drafts/${draftId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServerDrafts(prev => prev.filter(d => d.id !== draftId));
      toast.success('Draft deleted');
    } catch (error) {
      console.error('Error deleting draft:', error);
      toast.error('Failed to delete draft');
    }
  };
  
  const deleteDraftForCustomer = async (customerId) => {
    try {
      await axios.delete(`${API}/drafts/customer/${customerId}?draft_type=catalogue_order`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error deleting customer draft:', error);
    }
  };
  
  // Search Customers
  useEffect(() => {
    if (debouncedCustomerSearch.trim().length >= 2) {
      searchCustomers(debouncedCustomerSearch);
    } else {
      setCustomers([]);
    }
  }, [debouncedCustomerSearch]);
  
  const searchCustomers = async (query) => {
    const thisRequestId = ++customerSearchIdRef.current;
    setLoadingCustomers(true);
    try {
      const url = scopeToDistributorId
        ? `${API}/distributor-panel/customers?distributor_id=${scopeToDistributorId}&search=${encodeURIComponent(query)}`
        : `${API}/admin/customers/search?q=${encodeURIComponent(query)}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (thisRequestId === customerSearchIdRef.current) {
        setCustomers(response.data.customers || []);
      }
    } catch (error) {
      if (thisRequestId === customerSearchIdRef.current) {
        console.error('Error searching customers:', error);
        setCustomers([]);
      }
    } finally {
      if (thisRequestId === customerSearchIdRef.current) {
        setLoadingCustomers(false);
      }
    }
  };
  
  // Select Customer
  const handleSelectCustomer = async (customer) => {
    // Check if customer is from Bangladesh - redirect to Bangladesh Export Order flow
    if (isBangladeshCustomer(customer)) {
      setBangladeshCustomer(customer);
      setShowBangladeshModal(true);
      setCustomerSearch('');
      setCustomers([]);
      return; // Don't continue with domestic catalogue flow
    }
    
    setSelectedCustomer(customer);
    setCustomerDiscount(customer.discount_percentage || 0);
    setNewDiscount(customer.discount_percentage || 0);
    setCustomerSearch('');
    setCustomers([]);
    
    // Fetch special prices
    try {
      const response = await axios.get(`${API}/price-requests/admin/special-prices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const allSpecialPrices = response.data.special_prices || [];
      const customerSpecialPrices = {};
      allSpecialPrices
        .filter(sp => sp.customer_id === customer.customer_id)
        .forEach(sp => {
          customerSpecialPrices[sp.jp_part_no] = sp.special_price;
        });
      setSpecialPrices(customerSpecialPrices);
      
      if (Object.keys(customerSpecialPrices).length > 0) {
        toast.success(`Found ${Object.keys(customerSpecialPrices).length} special prices`);
      }
    } catch (error) {
      console.error('Error fetching special prices:', error);
      setSpecialPrices({});
    }
    
    // Load transporter preference
    const transporter = customer?.preferred_transporter || customer?.last_transporter;
    if (transporter) {
      setTransporterName(transporter);
    }
    
    setStep(2);
    fetchCategories();
  };
  
  // Update Customer Discount
  const handleUpdateDiscount = async () => {
    try {
      await axios.put(`${API}/admin/customers/${selectedCustomer.mobile}/discount`, 
        { discount_percentage: parseFloat(newDiscount) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomerDiscount(parseFloat(newDiscount));
      setSelectedCustomer(prev => ({ ...prev, discount_percentage: parseFloat(newDiscount) }));
      setEditingDiscount(false);
      toast.success('Discount updated');
    } catch (error) {
      toast.error('Failed to update discount');
    }
  };
  
  // Fetch Categories — use prefetch cache if available
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const { getCachedBrands } = await import('@/utils/prefetchCache');
      const cached = await getCachedBrands();
      if (cached && cached.length > 0) {
        setCategories(cached);
      } else {
        // 2026-06-19: admin order-creation flow opts into admin_only
        // categories (MONOGRAMS / CHILD PARTS) so admins can add those
        // parts on behalf of a customer. Backend silently strips the
        // flag for non-admin tokens — safe to always pass.
        const response = await axios.get(
          `${API}/categories/with-images?include_admin_only=true`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCategories(response.data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };
  
  // Fetch Products with infinite scroll
  const fetchProducts = useCallback(async (reset = false) => {
    if (!token || !selectedCustomer) return;
    if (loadingProducts && !reset) return;

    const newSkip = reset ? 0 : skip;
    // 2026-05: tag this fetch + capture the query string so stale
    // responses (slow short-prefix beating fast long-prefix) get dropped.
    const thisRequestId = ++productFetchIdRef.current;
    const thisSearch = debouncedCatalogueSearch.trim();
    setLoadingProducts(true);

    try {
      const params = new URLSearchParams({
        skip: newSkip.toString(),
        limit: ITEMS_PER_LOAD.toString(),
        // Admin order-creation flow opts in to deactivated master categories
        // so admins can fulfil legacy-brand requests (e.g. Tata/Mahindra
        // even after only Eicher remains active).
        include_inactive_brands: '1',
        // 2026-06-19: also opt into admin-only categories
        // (MONOGRAMS / CHILD PARTS) so admins can search/add them
        // while building a customer's order.
        include_admin_only: 'true'
      });

      if (thisSearch) {
        params.append('search', thisSearch);
      }

      const category = selectedCategory || 'All Products';
      const response = await axios.get(
        `${API}/categories/${encodeURIComponent(category)}/parts?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Guard 1: only the latest fetch wins.
      if (thisRequestId !== productFetchIdRef.current) return;
      // Guard 2: the search we kicked off still matches what's typed.
      if (thisSearch !== debouncedCatalogueSearch.trim()) return;

      const newProducts = response.data.parts || [];
      const total = response.data.total || 0;

      if (reset) {
        setProducts(newProducts);
        setSkip(ITEMS_PER_LOAD);
      } else {
        setProducts(prev => [...prev, ...newProducts]);
        setSkip(prev => prev + ITEMS_PER_LOAD);
      }

      setTotalProducts(total);
      setHasMore(newSkip + newProducts.length < total);

      // Prefetch images
      const imageFilenames = newProducts.filter(p => p.image).map(p => p.image);
      if (imageFilenames.length > 0 && prefetchImages) {
        prefetchImages(imageFilenames);
      }
    } catch (error) {
      if (thisRequestId === productFetchIdRef.current) {
        console.error('Error fetching products:', error);
      }
    } finally {
      if (thisRequestId === productFetchIdRef.current) {
        setLoadingProducts(false);
      }
    }
  }, [token, selectedCustomer, selectedCategory, debouncedCatalogueSearch, skip, loadingProducts, prefetchImages]);
  
  // Load products when category or search changes
  useEffect(() => {
    if (step === 2 && selectedCustomer) {
      setSkip(0);
      fetchProducts(true);
    }
  }, [selectedCategory, debouncedCatalogueSearch, step, selectedCustomer]);
  
  // Infinite scroll observer
  useEffect(() => {
    if (!loadMoreRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingProducts) {
          fetchProducts(false);
        }
      },
      { threshold: 0.1 }
    );
    
    observer.observe(loadMoreRef.current);
    observerRef.current = observer;
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loadingProducts, fetchProducts]);
  
  // Load product image
  const loadProductImage = async (partNo, imageName) => {
    if (getCachedImage) {
      const cached = getCachedImage(imageName);
      if (cached) {
        setProductImages(prev => ({ ...prev, [partNo]: cached }));
        return;
      }
    }
    
    try {
      if (fetchImage) {
        const dataUrl = await fetchImage(imageName);
        if (dataUrl) {
          setProductImages(prev => ({ ...prev, [partNo]: dataUrl }));
        }
      } else {
        const response = await axios.get(`${API}/image/${imageName}`);
        if (response.data.data_url) {
          setProductImages(prev => ({ ...prev, [partNo]: response.data.data_url }));
        }
      }
    } catch (error) {
      console.error('Error loading image:', error);
    }
  };
  
  // Cart Functions
  
  // Open Add to Cart dialog (instead of directly adding)
  const openAddToCartDialog = (product) => {
    setAddToCartProduct(product);
    // 2026-02-20 RC-151 — default qty to the warehouse MOQ so admin
    // isn't forced to type it (or, worse, submit qty=1 which would
    // be rejected server-side).
    const moq = Math.max(1, parseInt(product?.warehouse_moq || 1, 10));
    setAddToCartQuantity(moq);
    setAddToCartHandshakePrice('');
    // Focus quantity input after dialog opens
    setTimeout(() => {
      if (qtyInputRef.current) {
        qtyInputRef.current.focus();
        qtyInputRef.current.select();
      }
    }, 100);
  };
  
  // Confirm add to cart from dialog
  const confirmAddToCart = () => {
    if (!addToCartProduct) return;
    if (addToCartQuantity <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    
    // Check if item already exists in cart
    const existing = cart.find(item => item.jp_part_no === addToCartProduct.jp_part_no);
    if (existing) {
      // Show confirmation dialog for duplicate item
      setDuplicateItemConfirm({
        product: addToCartProduct,
        existingQty: existing.quantity,
        newQty: addToCartQuantity,
        handshakePrice: addToCartHandshakePrice.trim()
      });
      return;
    }
    
    // No duplicate - add directly
    addItemToCart(addToCartProduct, addToCartQuantity, addToCartHandshakePrice.trim());
  };
  
  // Actual function to add/merge item to cart
  const addItemToCart = (product, quantity, handshakePrice, mergeWithExisting = false) => {
    // Store handshake price if entered
    if (handshakePrice && parseFloat(handshakePrice) > 0) {
      setInlineHandshakePrices(prev => ({
        ...prev,
        [product.jp_part_no]: parseFloat(handshakePrice)
      }));
    }

    // 2026-02-20 RC-151 — Snap the incoming qty to a warehouse_moq
    // multiple before insert / merge. Blank/0 caller qty falls back
    // to `moq` (one full pack).
    const moq = Math.max(1, parseInt(product.warehouse_moq || 1, 10));
    const snapQty = (q) => {
      const n = Math.max(1, parseInt(q || 0, 10) || moq);
      if (moq <= 1) return n;
      return Math.ceil(n / moq) * moq;
    };
    const snappedQty = snapQty(quantity);

    const existing = cart.find(item => item.jp_part_no === product.jp_part_no);
    if (existing && mergeWithExisting) {
      const merged = snapQty(existing.quantity + snappedQty);
      setCart(cart.map(item =>
        item.jp_part_no === product.jp_part_no
          ? { ...item, quantity: merged }
          : item
      ));
      toast.success(`Updated ${product.part_name?.substring(0, 30)}: ${existing.quantity} + ${snappedQty} = ${merged} PC`);
    } else if (!existing) {
      setCart([...cart, { ...product, quantity: snappedQty }]);
      toast.success(`Added ${snappedQty} × ${product.part_name?.substring(0, 30)}...`);
    }

    // Reset state
    setAddToCartProduct(null);
    setAddToCartQuantity(0);
    setAddToCartHandshakePrice('');
    setDuplicateItemConfirm(null);
  };
  
  // Handle duplicate item confirmation
  const handleDuplicateConfirm = (merge) => {
    if (!duplicateItemConfirm) return;
    
    if (merge) {
      // Merge quantities
      addItemToCart(
        duplicateItemConfirm.product, 
        duplicateItemConfirm.newQty, 
        duplicateItemConfirm.handshakePrice, 
        true
      );
    } else {
      // Cancel - just close dialogs
      setDuplicateItemConfirm(null);
      setAddToCartProduct(null);
      setAddToCartQuantity(0);
      setAddToCartHandshakePrice('');
    }
  };
  
  // Legacy addToCart for quick add (shows confirmation for duplicates)
  const addToCart = (product, qty = 1) => {
    const existing = cart.find(item => item.jp_part_no === product.jp_part_no);
    if (existing) {
      // Show confirmation for duplicate
      setDuplicateItemConfirm({
        product: product,
        existingQty: existing.quantity,
        newQty: qty,
        handshakePrice: ''
      });
    } else {
      setCart([...cart, { ...product, quantity: qty }]);
      toast.success(`Added ${qty} x ${product.part_name?.substring(0, 30)}...`);
    }
  };
  
  // Flag/Mark Product Functions
  const handleMarkForReview = (product, e) => {
    if (e) e.stopPropagation();
    if (marking) return;
    
    const isMarked = markedProducts[product.jp_part_no] || product.marked_for_review;
    
    if (isMarked) {
      // Unmark directly
      submitUnmark(product);
    } else {
      // Show dialog to select reasons
      setMarkingProduct(product);
      setSelectedReasons([]);
      setShowMarkDialog(true);
    }
  };
  
  const submitMark = async () => {
    if (!markingProduct || selectedReasons.length === 0) {
      toast.error('Please select at least one reason');
      return;
    }
    
    setMarking(true);
    setShowMarkDialog(false);
    
    try {
      const encodedPartNo = encodeURIComponent(markingProduct.jp_part_no).replace(/%2F/gi, '%252F');
      await axios.post(`${API}/admin/products/${encodedPartNo}/mark`, 
        { reasons: selectedReasons },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setMarkedProducts(prev => ({
        ...prev,
        [markingProduct.jp_part_no]: { marked: true, reasons: selectedReasons }
      }));
      
      toast.success('Product marked for review');
    } catch (error) {
      toast.error('Failed to mark product');
    } finally {
      setMarking(false);
      setMarkingProduct(null);
    }
  };
  
  const submitUnmark = async (product) => {
    setMarking(true);
    
    try {
      const encodedPartNo = encodeURIComponent(product.jp_part_no).replace(/%2F/gi, '%252F');
      await axios.post(`${API}/admin/products/${encodedPartNo}/unmark`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setMarkedProducts(prev => ({
        ...prev,
        [product.jp_part_no]: { marked: false, reasons: [] }
      }));
      
      toast.success('Product unmarked');
    } catch (error) {
      toast.error('Failed to unmark product');
    } finally {
      setMarking(false);
    }
  };
  
  const toggleReason = (reason) => {
    setSelectedReasons(prev => 
      prev.includes(reason) 
        ? prev.filter(r => r !== reason)
        : [...prev, reason]
    );
  };
  
  const updateCartQuantity = (partNo, delta) => {
    setCart(cart.map(item => {
      if (item.jp_part_no === partNo) {
        // 2026-02-20 RC-151 — snap-then-step. If current qty isn't
        // already on a moq boundary (legacy add / hydrated line),
        // the first click of + or − aligns qty onto the nearest
        // valid multiple.
        const step = Math.max(1, parseInt(item.warehouse_moq || 1, 10));
        const current = parseInt(item.quantity || 0, 10);
        const isClean = step <= 1 || current % step === 0;
        let newQty;
        if (isClean) {
          newQty = Math.max(step, current + (delta > 0 ? step : -step));
        } else {
          newQty = delta > 0
            ? Math.ceil(current / step) * step
            : Math.max(step, Math.floor(current / step) * step);
          if (newQty === current) {
            newQty = Math.max(step, current + (delta > 0 ? step : -step));
          }
        }
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const setCartQuantityDirect = (partNo, value) => {
    const qty = parseInt(value) || 1;
    setCart(cart.map(item => {
      if (item.jp_part_no === partNo) {
        // 2026-02-20 RC-151 — Round UP to nearest warehouse_moq
        // multiple on direct edit (matches server-side snap_qty_up_to_moq).
        const moq = Math.max(1, parseInt(item.warehouse_moq || 1, 10));
        const snapped = moq > 1
          ? Math.ceil(Math.max(1, qty) / moq) * moq
          : Math.max(1, qty);
        return { ...item, quantity: snapped };
      }
      return item;
    }));
  };
  
  const removeFromCart = (partNo) => {
    setCart(cart.filter(item => item.jp_part_no !== partNo));
    setInlineHandshakePrices(prev => {
      const updated = { ...prev };
      delete updated[partNo];
      return updated;
    });
  };
  
  const getCartItemCount = (partNo) => {
    const item = cart.find(i => i.jp_part_no === partNo);
    return item ? item.quantity : 0;
  };
  
  // Price calculations
  const calculateItemPrice = (item) => {
    const mrp = item.mrp || 0;
    let discountedPrice;
    
    if (inlineHandshakePrices[item.jp_part_no]) {
      discountedPrice = inlineHandshakePrices[item.jp_part_no];
    } else if (specialPrices[item.jp_part_no]) {
      discountedPrice = specialPrices[item.jp_part_no];
    } else {
      discountedPrice = mrp * (1 - customerDiscount / 100);
    }
    return discountedPrice * item.quantity;
  };
  
  const getItemPrice = (item) => {
    const mrp = item.mrp || 0;
    if (inlineHandshakePrices[item.jp_part_no]) {
      return inlineHandshakePrices[item.jp_part_no];
    }
    if (specialPrices[item.jp_part_no]) {
      return specialPrices[item.jp_part_no];
    }
    return mrp * (1 - customerDiscount / 100);
  };
  
  const hasSpecialPrice = (item) => {
    return !!specialPrices[item.jp_part_no] || !!inlineHandshakePrices[item.jp_part_no];
  };
  
  const cartTotal = cart.reduce((sum, item) => sum + calculateItemPrice(item), 0);
  const totalCartItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  const calculateTotal = () => cartTotal;
  
  // Submit Order
  const handleSubmitOrder = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    
    if (dispatchMode !== 'local' && !transporterName.trim()) {
      toast.error('Transporter name is required for Transport/Courier dispatch');
      return;
    }

    setSubmitting(true);
    try {
      // Save handshake prices
      if (Object.keys(inlineHandshakePrices).length > 0) {
        try {
          const specialPriceItems = Object.entries(inlineHandshakePrices).map(([partNo, price]) => {
            const cartItem = cart.find(item => item.jp_part_no === partNo);
            return {
              jp_part_no: partNo,
              part_name: cartItem?.part_name || partNo,
              original_price: cartItem?.mrp || 0,
              special_price: price
            };
          });
          
          await axios.post(`${API}/price-requests/admin/create-special-prices`, {
            customer_id: selectedCustomer.customer_id,
            items: specialPriceItems
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          toast.success(`${specialPriceItems.length} handshake price(s) saved`);
        } catch (err) {
          console.error('Error saving handshake prices:', err);
        }
      }
      
      const orderData = {
        customer_mobile: selectedCustomer.mobile,
        items: cart.map(item => {
          let effectivePrice;
          let hasSpecial = false;
          
          if (inlineHandshakePrices[item.jp_part_no]) {
            effectivePrice = inlineHandshakePrices[item.jp_part_no];
            hasSpecial = true;
          } else if (specialPrices[item.jp_part_no]) {
            effectivePrice = specialPrices[item.jp_part_no];
            hasSpecial = true;
          } else {
            effectivePrice = item.mrp * (1 - customerDiscount / 100);
          }
          
          return {
            jp_part_no: item.jp_part_no,
            part_name: item.part_name,
            quantity: item.quantity,
            price: item.mrp,
            discounted_price: effectivePrice,
            has_special_price: hasSpecial
          };
        }),
        discount_percentage: customerDiscount,
        dispatch_mode: dispatchMode,
        preferred_transporter: dispatchMode === 'local' ? '' : transporterName,
        dispatch_remarks: dispatchRemarks,
        notes: `Order created via Catalogue by admin`
      };

      await axios.post(`${API}/admin/orders/create-for-customer`, orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (selectedCustomer?.customer_id) {
        await deleteDraftForCustomer(selectedCustomer.customer_id);
      }

      toast.success('Order created successfully!');
      localStorage.removeItem(CATALOGUE_ORDER_DRAFT_KEY);
      onClose();
      resetForm();
    } catch (error) {
      console.error('Error creating order:', error);
      // 2026-02-20 RC-151 — Show detailed per-line warehouse-MOQ
      // violations returned by the backend so the admin can see
      // exactly which qtys need to be adjusted.
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'WAREHOUSE_MOQ_VIOLATION') {
        const lines = (detail.violations || [])
          .map(v => `${v.jp_part_no}: entered ${v.qty} → must be multiple of ${v.moq} (try ${v.suggested_qty})`)
          .join('\n');
        toast.error(`Warehouse MOQ violation:\n${lines}`, { duration: 10000 });
      } else {
        const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to create order');
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };
  
  const resetForm = () => {
    setStep(1);
    setCustomerSearch('');
    setCustomers([]);
    setSelectedCustomer(null);
    setCustomerDiscount(0);
    setSpecialPrices({});
    setInlineHandshakePrices({});
    setCatalogueSearch('');
    setProducts([]);
    setSelectedCategory(null);
    setCart([]);
    setDispatchMode('transport');
    setTransporterName('');
    setDispatchRemarks('');
    setSkip(0);
    localStorage.removeItem(CATALOGUE_ORDER_DRAFT_KEY);
  };
  
  const handleCloseButtonClick = () => {
    if (hasUnsavedChanges) {
      setShowCloseConfirm(true);
    } else {
      resetForm();
      onClose();
    }
  };
  
  const confirmClose = () => {
    setShowCloseConfirm(false);
    resetForm();
    onClose();
  };
  
  const handleClose = () => {
    // Prevent closing via outside click
  };
  
  // Render Product Card for Catalogue
  const renderProductCard = (product) => {
    const cartQty = getCartItemCount(product.jp_part_no);
    const unitPrice = product.mrp || 0;
    const hasSpecial = !!specialPrices[product.jp_part_no];
    const hasHandshake = !!inlineHandshakePrices[product.jp_part_no];
    const discountedPrice = hasHandshake
      ? inlineHandshakePrices[product.jp_part_no]
      : hasSpecial 
        ? specialPrices[product.jp_part_no]
        : unitPrice * (1 - customerDiscount / 100);
    const imageUrl = product.image ? `${API}/images/part/${product.image}` : null;
    const isMarked = markedProducts[product.jp_part_no]?.marked || product.marked_for_review;
    
    return (
      <div key={product.jp_part_no} className="bg-white rounded-lg border shadow-sm hover:shadow-md transition-all overflow-hidden relative">
        {/* Flag Button - Top Left */}
        {canMarkProducts && (
          <button
            onClick={(e) => handleMarkForReview(product, e)}
            disabled={marking}
            className={`absolute top-2 left-2 z-10 p-1.5 rounded-full shadow-md transition-all ${
              isMarked 
                ? 'bg-orange-500 text-white hover:bg-orange-600' 
                : 'bg-white text-gray-400 hover:text-orange-500 hover:bg-orange-50'
            }`}
            title={isMarked ? 'Unmark product' : 'Flag product for review'}
          >
            <Flag className="w-3.5 h-3.5" />
          </button>
        )}
        
        {/* Image - Clickable for zoom */}
        <div 
          className={`aspect-square bg-gray-50 relative flex items-center justify-center p-2 ${imageUrl ? 'cursor-zoom-in' : ''}`}
          onClick={() => {
            if (imageUrl) {
              setLightboxImage(imageUrl);
              setLightboxAlt(product.part_name);
            }
          }}
        >
          {imageUrl ? (
            <img src={imageUrl} alt={product.part_name} className="max-h-full max-w-full object-contain" 
              loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
          ) : (
            <Package className="w-12 h-12 text-gray-300" />
          )}
          
          {/* Cart quantity badge */}
          {cartQty > 0 && (
            <div className="absolute top-2 right-2 bg-green-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
              {cartQty}
            </div>
          )}
          
          {/* Marked indicator */}
          {isMarked && (
            <div className="absolute bottom-1 left-1 bg-orange-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
              FLAGGED
            </div>
          )}
        </div>
        
        {/* Info */}
        <div className="p-3">
          <h4 className="font-medium text-gray-900 text-sm line-clamp-2 min-h-[40px]">{product.part_name}</h4>
          <p className="text-xs text-gray-500 mt-1">
            <span className="font-mono text-red-600 font-bold">{formatJpWithAlias(product)}</span>
          </p>
          <div className="mt-1"><StockBadge jpPartNo={product.jp_part_no} size="sm" /></div>
          
          {/* Price */}
          <div className="mt-2">
            {(customerDiscount > 0 || hasSpecial || hasHandshake) ? (
              <div className="flex flex-col">
                <span className="text-xs text-gray-400 line-through">₹{unitPrice.toFixed(2)}</span>
                <span className={`text-sm font-bold ${hasHandshake ? 'text-blue-600' : hasSpecial ? 'text-amber-600' : 'text-green-600'}`}>
                  ₹{discountedPrice.toFixed(2)}
                </span>
              </div>
            ) : (
              <span className="text-sm font-bold text-gray-900">₹{unitPrice.toFixed(2)}</span>
            )}
            <div><MrpEffectiveDate part={product} /></div>
            {/* MrpChangeBadge removed 2026-05-30 (see PartCard.js) */}
            {hasSpecial && !hasHandshake && (
              <span className="text-[10px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded">Special</span>
            )}
            {hasHandshake && (
              <span className="text-[10px] bg-blue-100 text-blue-700 px-1 py-0.5 rounded">Handshake</span>
            )}
          </div>
          
          {/* Add to cart button - Opens dialog */}
          <div className="mt-3 flex items-center gap-2">
            {cartQty > 0 ? (
              <div className="flex items-center gap-1 w-full">
                <Button size="sm" variant="outline" onClick={() => updateCartQuantity(product.jp_part_no, -1)} className="h-8 w-8 p-0">
                  <Minus className="w-3 h-3" />
                </Button>
                <span className="flex-1 text-center font-bold">{cartQty}</span>
                <Button size="sm" variant="outline" onClick={() => openAddToCartDialog(product)} className="h-8 px-2 text-xs">
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            ) : (
              <Button 
                size="sm" 
                onClick={() => openAddToCartDialog(product)} 
                className="w-full bg-red-600 hover:bg-red-700 text-white h-8"
              >
                <Plus className="w-3 h-3 mr-1" /> Add
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Main Dialog */}
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent 
          className="max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] overflow-hidden bg-white p-0 flex flex-col"
          preventClose={true}
          onCloseClick={handleCloseButtonClick}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-red-600 to-red-700 text-white">
            <div className="flex items-center gap-3">
              <ShoppingCart className="w-6 h-6" />
              <div>
                <h2 className="font-bold text-lg">Create Order via Catalogue</h2>
                {selectedCustomer && (
                  <p className="text-sm text-red-100">
                    For: {selectedCustomer.company_name || selectedCustomer.name}
                  </p>
                )}
              </div>
              {hasUnsavedChanges && (
                <Badge className="bg-amber-500 text-white text-xs">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Unsaved
                </Badge>
              )}
            </div>
            
            {/* Cart button */}
            {step === 2 && cart.length > 0 && (
              <Button 
                onClick={() => setStep(3)} 
                className="bg-white text-red-600 hover:bg-red-50"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Cart ({totalCartItems}) - ₹{cartTotal.toFixed(0)}
              </Button>
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Step 1: Select Customer */}
            {step === 1 && (
              <div className="p-6 space-y-4 max-w-2xl mx-auto">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Select Customer</h3>
                
                {/* Server Drafts */}
                {serverDrafts.length > 0 && (
                  <Card className="border-amber-200 bg-amber-50 mb-4">
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
                        <Clock className="w-4 h-4" />
                        Pending for Completion ({serverDrafts.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-2 space-y-2 max-h-48 overflow-y-auto">
                      {serverDrafts.map((draft) => (
                        <div key={draft.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">
                              {draft.company_name || draft.customer_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {draft.item_count} items • ₹{(draft.total_amount || 0).toLocaleString('en-IN')}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white h-8" onClick={() => resumeDraft(draft)}>
                              <Play className="w-3 h-3 mr-1" /> Resume
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600 h-8 w-8 p-0" onClick={() => deleteServerDraft(draft.id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
                
                {loadingDrafts && (
                  <div className="text-center py-2 text-gray-500 text-sm">
                    <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
                    Loading saved drafts...
                  </div>
                )}
                
                {/* Customer Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    placeholder="Search by customer name or company..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="pl-10 h-12 text-lg"
                    autoFocus
                  />
                </div>
                
                {loadingCustomers && (
                  <div className="text-center py-4 text-gray-500">Searching...</div>
                )}
                
                {customers.length > 0 && (
                  <div className="border rounded-lg max-h-80 overflow-y-auto">
                    {customers.map((customer) => (
                      <div
                        key={customer.mobile}
                        onClick={() => handleSelectCustomer(customer)}
                        className={`p-4 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer transition-colors ${
                          isBangladeshCustomer(customer) ? 'bg-green-50 hover:bg-green-100 border-l-4 border-l-green-500' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              {isBangladeshCustomer(customer) && <span className="text-lg">🇧🇩</span>}
                              <User className="w-4 h-4 text-gray-500" />
                              <span className="font-medium text-gray-900">{customer.name}</span>
                            </div>
                            {customer.company_name && (
                              <div className="flex items-center gap-2 mt-1">
                                <Building2 className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-600">{customer.company_name}</span>
                              </div>
                            )}
                            <div className="text-sm text-gray-500 mt-1">
                              {customer.country_code ? `+${customer.country_code} ` : ''}{customer.mobile}
                            </div>
                          </div>
                          <div className="text-right">
                            {isBangladeshCustomer(customer) ? (
                              <Badge className="bg-green-600 text-white text-xs">Bangladesh Export</Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs">
                                {customer.category || 'RETAILER'}
                              </Badge>
                            )}
                            {customer.discount_percentage > 0 && (
                              <div className="text-sm text-green-600 mt-1">
                                {customer.discount_percentage}% discount
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {customerSearch.length >= 2 && customers.length === 0 && !loadingCustomers && (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">No customers found matching "{customerSearch}"</p>
                    <Button 
                      variant="outline" 
                      onClick={() => setShowQuickRegister(true)}
                      className="border-blue-300 text-blue-600 hover:bg-blue-50"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Register New Customer
                    </Button>
                  </div>
                )}
                
                {/* Quick Register Button - always visible */}
                <div className="mt-4 pt-4 border-t">
                  <Button 
                    variant="ghost" 
                    onClick={() => setShowQuickRegister(true)}
                    className="w-full text-blue-600 hover:bg-blue-50"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Register New Customer
                  </Button>
                </div>
              </div>
            )}
            
            {/* Step 2: Browse Catalogue */}
            {step === 2 && selectedCustomer && (
              <div className="flex flex-col h-full">
                {/* Customer Info Bar */}
                <div className="bg-blue-50 border-b px-4 py-2 flex items-center justify-between flex-shrink-0">
                  <div className="flex items-center gap-4">
                    <div>
                      <span className="font-medium text-gray-900">{selectedCustomer.company_name || selectedCustomer.name}</span>
                      <span className="text-sm text-gray-500 ml-2">({selectedCustomer.mobile})</span>
                    </div>
                    {editingDiscount ? (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={newDiscount}
                          onChange={(e) => setNewDiscount(e.target.value)}
                          className="w-16 h-7 text-sm"
                          min="0"
                          max="100"
                        />
                        <span className="text-sm">%</span>
                        <Button size="sm" onClick={handleUpdateDiscount} className="bg-green-600 hover:bg-green-700 h-7 w-7 p-0">
                          <Check className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingDiscount(false)} className="h-7 w-7 p-0">
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <Badge className="bg-green-100 text-green-700 cursor-pointer" onClick={() => setEditingDiscount(true)}>
                        <Percent className="w-3 h-3 mr-1" />
                        {customerDiscount}% discount
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={saveProgressToServer}
                      disabled={cart.length === 0 || savingDraft}
                      className="border-amber-500 text-amber-700 hover:bg-amber-50"
                    >
                      <Save className="w-3 h-3 mr-1" />
                      {savingDraft ? 'Saving...' : 'Save Progress'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setSelectedCustomer(null); setStep(1); }}>
                      Change Customer
                    </Button>
                  </div>
                </div>
                
                {/* Search and Category Filter */}
                <div className="bg-white border-b px-4 py-3 flex items-center gap-4 flex-shrink-0">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search products..."
                      value={catalogueSearch}
                      onChange={(e) => setCatalogueSearch(e.target.value)}
                      className="pl-9 h-10"
                    />
                    {catalogueSearch && (
                      <button
                        onClick={() => setCatalogueSearch('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  {/* Category selector */}
                  <select
                    value={selectedCategory || ''}
                    onChange={(e) => setSelectedCategory(e.target.value || null)}
                    className="h-10 px-3 border rounded-md text-sm bg-white"
                  >
                    <option value="">All Products</option>
                    {categories.map(cat => (
                      <option key={cat.name} value={cat.name}>{cat.name} ({cat.parts_count})</option>
                    ))}
                  </select>
                  
                  <div className="text-sm text-gray-500">
                    {totalProducts.toLocaleString()} products
                  </div>
                </div>
                
                {/* Products Grid with Infinite Scroll */}
                <div ref={productContainerRef} className="flex-1 overflow-y-auto p-4">
                  {loadingProducts && products.length === 0 ? (
                    <div className="flex items-center justify-center py-20">
                      <Loader2 className="w-8 h-8 animate-spin text-red-600" />
                      <span className="ml-3 text-gray-600">Loading products...</span>
                    </div>
                  ) : products.length > 0 ? (
                    <>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                        {products.map(renderProductCard)}
                      </div>
                      
                      {/* Load more trigger */}
                      <div ref={loadMoreRef} className="py-8 text-center">
                        {loadingProducts && (
                          <div className="flex items-center justify-center gap-2 text-gray-500">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Loading more...
                          </div>
                        )}
                        {!hasMore && products.length > 0 && (
                          <p className="text-gray-400 text-sm">All products loaded</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-20">
                      <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">
                        {catalogueSearch ? `No products found matching "${catalogueSearch}"` : 'No products available'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Step 3: Review Cart & Checkout */}
            {step === 3 && (
              <div className="p-6 max-w-4xl mx-auto space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Button variant="ghost" onClick={() => setStep(2)} className="-ml-2">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Catalogue
                  </Button>
                </div>
                
                <Card>
                  <CardHeader className="py-3 bg-gray-50">
                    <CardTitle className="text-lg">Order Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-500">Customer</Label>
                        <div className="font-medium">{selectedCustomer?.name}</div>
                        {selectedCustomer?.company_name && (
                          <div className="text-sm text-gray-600">{selectedCustomer.company_name}</div>
                        )}
                      </div>
                      <div>
                        <Label className="text-gray-500">Phone</Label>
                        <div className="font-medium">{selectedCustomer?.mobile}</div>
                      </div>
                      <div>
                        <Label className="text-gray-500">Discount</Label>
                        <div className="font-medium text-green-600">{customerDiscount}%</div>
                      </div>
                      <div>
                        <Label className="text-gray-500">Total Items</Label>
                        <div className="font-medium">{totalCartItems}</div>
                      </div>
                    </div>
                    
                    {/* Cart Items */}
                    <div className="border-t pt-4">
                      <Label className="text-gray-500 mb-2 block">Items ({cart.length})</Label>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {cart.map((item) => (
                          <div key={item.jp_part_no} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm truncate">{item.part_name}</div>
                              <div className="text-xs text-gray-500">{formatJpWithAlias(item)}</div>
                              <StockBadge jpPartNo={item.jp_part_no} size="sm" />
                              {/* 2026-02-20 RC-151 · warehouse-MOQ pack-of hint. */}
                              {parseInt(item.warehouse_moq || 1, 10) > 1 && (
                                <div
                                  className="text-[10px] text-amber-700 font-medium"
                                  data-testid={`wh-moq-hint-${item.jp_part_no}`}
                                >
                                  MOQ {item.warehouse_moq} · pack of {item.warehouse_moq}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1">
                                <Button size="sm" variant="outline" onClick={() => updateCartQuantity(item.jp_part_no, -1)} className="h-7 w-7 p-0">
                                  <Minus className="w-3 h-3" />
                                </Button>
                                <Input
                                  type="number"
                                  min="1"
                                  value={item.quantity}
                                  onChange={(e) => setCartQuantityDirect(item.jp_part_no, e.target.value)}
                                  className="w-14 h-7 text-center text-sm"
                                />
                                <Button size="sm" variant="outline" onClick={() => updateCartQuantity(item.jp_part_no, 1)} className="h-7 w-7 p-0">
                                  <Plus className="w-3 h-3" />
                                </Button>
                              </div>
                              <div className="w-20 text-right font-medium">
                                ₹{calculateItemPrice(item).toFixed(0)}
                              </div>
                              <Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0" onClick={() => removeFromCart(item.jp_part_no)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Total */}
                    <div className="border-t pt-4 flex justify-between items-center">
                      <span className="text-xl font-bold">Grand Total</span>
                      <span className="text-2xl font-bold text-green-600">₹{cartTotal.toFixed(2)}</span>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Dispatch Details */}
                <Card>
                  <CardHeader className="py-3 bg-blue-50">
                    <CardTitle className="text-lg">Dispatch Details</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-4">
                    {isDelhiCustomer && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-sm text-green-800 font-medium flex items-center gap-2">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                          Delhi Customer — Dispatch locked to Local
                          {isMasterAdmin && <span className="text-xs text-amber-600 ml-2">(Master Admin can override)</span>}
                        </p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="dispatch_mode">Dispatch Method *</Label>
                        <select
                          id="dispatch_mode"
                          value={dispatchMode}
                          onChange={(e) => setDispatchMode(e.target.value)}
                          disabled={isDispatchLocked}
                          className={`w-full mt-1 px-3 py-2 border rounded-md ${isDispatchLocked ? 'bg-gray-100 cursor-not-allowed opacity-75' : ''}`}
                          data-testid="dispatch-mode-select"
                        >
                          <option value="transport">Transport</option>
                          <option value="courier">Courier</option>
                          <option value="local">Local Dispatch (Loose)</option>
                        </select>
                      </div>
                      
                      {dispatchMode !== 'local' && (
                        <div>
                          <Label htmlFor="transporter_name">{dispatchMode === 'courier' ? 'Courier' : 'Transporter'} Name *</Label>
                          <TransporterComboBox
                            value={transporterName}
                            onChange={setTransporterName}
                            customerId={selectedCustomer?.customer_id}
                            dispatchMode={dispatchMode}
                            placeholder={dispatchMode === 'courier' ? 'Enter courier name' : 'Enter transporter name'}
                          />
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="dispatch_remarks">Remarks (Optional)</Label>
                      <Input
                        id="dispatch_remarks"
                        value={dispatchRemarks}
                        onChange={(e) => setDispatchRemarks(e.target.value)}
                        className="mt-1"
                        placeholder="Any additional notes..."
                      />
                    </div>
                  </CardContent>
                </Card>
                
                {/* Actions */}
                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={() => setStep(2)}>
                    Back to Catalogue
                  </Button>
                  <Button 
                    onClick={handleSubmitOrder} 
                    disabled={submitting || cart.length === 0}
                    className="bg-green-600 hover:bg-green-700 px-8"
                  >
                    {submitting ? 'Creating Order...' : 'Create Order'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Close Confirmation Dialog */}
      <Dialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <DialogContent className="max-w-md bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Unsaved Changes
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">You have unsaved items in your cart. Are you sure you want to close?</p>
            <p className="text-sm text-gray-500 mt-2">Your progress will be lost.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCloseConfirm(false)}>
              Continue Shopping
            </Button>
            <Button onClick={confirmClose} className="bg-red-600 hover:bg-red-700 text-white">
              Discard & Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Draft Recovery Dialog */}
      <Dialog open={showDraftRecovery} onOpenChange={setShowDraftRecovery}>
        <DialogContent className="max-w-md bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-red-600" />
              Restore Previous Order?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">You have an unsaved order from your previous session.</p>
            {savedDraft && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
                <p className="text-gray-700">
                  <strong>Customer:</strong> {savedDraft.cs?.cn || savedDraft.cs?.n || 'Not selected'}
                </p>
                <p className="text-gray-700">
                  <strong>Items:</strong> {savedDraft.cc ? Object.keys(savedDraft.cc).length : 0}
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={discardDraft}>
              Start Fresh
            </Button>
            <Button onClick={restoreDraft} className="bg-red-600 hover:bg-red-700 text-white">
              Restore Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add to Cart Dialog */}
      <Dialog open={!!addToCartProduct} onOpenChange={() => setAddToCartProduct(null)}>
        <DialogContent className="sm:max-w-md bg-white border-2 border-gray-200 shadow-xl max-h-[90vh] overflow-y-auto"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            setTimeout(() => {
              if (qtyInputRef.current) {
                qtyInputRef.current.focus();
                qtyInputRef.current.select();
              }
            }, 0);
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-gray-900">Add to Cart</DialogTitle>
          </DialogHeader>
          
          {addToCartProduct && (() => {
            const existingSpecialPrice = specialPrices[addToCartProduct.jp_part_no];
            const defaultPrice = existingSpecialPrice || (addToCartProduct.mrp || 0) * (1 - customerDiscount / 100);
            const handshakeValue = addToCartHandshakePrice.trim();
            const effectivePrice = (handshakeValue && parseFloat(handshakeValue) > 0) 
              ? parseFloat(handshakeValue) 
              : defaultPrice;
            const hasHandshake = handshakeValue && parseFloat(handshakeValue) > 0;
            
            return (
              <div className="space-y-4">
                {/* Product Preview */}
                <div className="flex gap-4">
                  <div className="w-20 h-20 bg-gray-50 rounded flex-shrink-0 flex items-center justify-center">
                    {productImages[addToCartProduct.jp_part_no] ? (
                      <img 
                        src={productImages[addToCartProduct.jp_part_no]} 
                        alt={addToCartProduct.part_name}
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <Package className="w-8 h-8 text-gray-300" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 text-sm">{addToCartProduct.part_name}</h4>
                    <p className="text-xs text-gray-500">Part No: <span className="font-mono text-red-600">{formatJpWithAlias(addToCartProduct)}</span></p>
                    <div className="mt-1">
                      <span className="text-xs text-gray-400">MRP: ₹{(addToCartProduct.mrp || 0).toFixed(2)}</span>
                      {customerDiscount > 0 && (
                        <span className="text-xs text-green-600 ml-2">({customerDiscount}% discount)</span>
                      )}
                    </div>
                    {existingSpecialPrice && !hasHandshake && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded mt-1 inline-block">
                        Existing Special: ₹{existingSpecialPrice.toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Quantity and Handshake Price Row */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Quantity Selection */}
                  <div className="space-y-2">
                    <Label className="text-gray-700 font-medium">
                      Quantity
                      {parseInt(addToCartProduct?.warehouse_moq || 1, 10) > 1 && (
                        <span
                          className="ml-2 text-xs font-semibold text-amber-700"
                          data-testid="add-dialog-wh-moq-hint"
                        >
                          · MOQ {addToCartProduct.warehouse_moq} · pack of {addToCartProduct.warehouse_moq}
                        </span>
                      )}
                    </Label>
                    <div className="flex items-center gap-2">
                      {/* 2026-02-20 RC-151 — +/- and direct edit both
                          step by / snap to warehouse_moq. */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const moq = Math.max(1, parseInt(addToCartProduct?.warehouse_moq || 1, 10));
                          setAddToCartQuantity(Math.max(moq, addToCartQuantity - moq));
                        }}
                        className="h-10 w-10 p-0"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <Input
                        ref={qtyInputRef}
                        type="number"
                        min="0"
                        value={addToCartQuantity}
                        onChange={(e) => {
                          const raw = Math.max(0, parseInt(e.target.value) || 0);
                          const moq = Math.max(1, parseInt(addToCartProduct?.warehouse_moq || 1, 10));
                          if (moq <= 1 || raw === 0) {
                            setAddToCartQuantity(raw);
                          } else {
                            // Round UP to the next multiple.
                            setAddToCartQuantity(Math.ceil(raw / moq) * moq);
                          }
                        }}
                        onFocus={(e) => e.target.select()}
                        autoFocus
                        className="w-20 h-10 text-center text-lg font-bold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const moq = Math.max(1, parseInt(addToCartProduct?.warehouse_moq || 1, 10));
                          const current = parseInt(addToCartQuantity || 0, 10);
                          const isClean = moq <= 1 || current % moq === 0;
                          if (isClean) {
                            setAddToCartQuantity(Math.max(moq, current + moq));
                          } else {
                            // Snap up first click, then step.
                            setAddToCartQuantity(Math.ceil(current / moq) * moq);
                          }
                        }}
                        className="h-10 w-10 p-0"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Handshake Price Input */}
                  <div className="space-y-2">
                    <Label className="text-gray-700 font-medium">
                      Handshake Price <span className="text-xs text-gray-400">(optional)</span>
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={addToCartHandshakePrice}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '' || /^\d*\.?\d*$/.test(val)) {
                            setAddToCartHandshakePrice(val);
                          }
                        }}
                        onFocus={(e) => e.target.select()}
                        placeholder={defaultPrice.toFixed(2)}
                        className="h-10 pl-7 text-lg font-bold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                    {hasHandshake && (
                      <p className="text-xs text-blue-600">Will be saved as special price</p>
                    )}
                  </div>
                </div>

                {/* Total */}
                <div className={`rounded-lg p-3 flex justify-between items-center ${hasHandshake ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}>
                  <span className="font-medium text-gray-700">
                    Unit Price: <span className={`font-bold ${hasHandshake ? 'text-blue-600' : existingSpecialPrice ? 'text-amber-600' : 'text-green-600'}`}>₹{effectivePrice.toFixed(2)}</span>
                  </span>
                  <span className={`text-xl font-bold ${hasHandshake ? 'text-blue-600' : existingSpecialPrice ? 'text-amber-600' : 'text-green-600'}`}>
                    Total: ₹{(effectivePrice * addToCartQuantity).toFixed(2)}
                  </span>
                </div>
              </div>
            );
          })()}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAddToCartProduct(null)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmAddToCart}
              disabled={addToCartQuantity <= 0}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Add to Cart
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Mark Product Reasons Dialog */}
      {canMarkProducts && (
        <Dialog open={showMarkDialog} onOpenChange={setShowMarkDialog}>
          <DialogContent className="sm:max-w-md bg-white border-2 border-orange-200 shadow-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Flag className="w-5 h-5 text-orange-600" />
                Mark Product for Review
              </DialogTitle>
              <DialogDescription>
                Select the reason(s) for flagging this product
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4 space-y-3">
              {['Image', 'MRP', 'Description', 'Part Number'].map((reason) => (
                <label
                  key={reason}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedReasons.includes(reason)
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-orange-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedReasons.includes(reason)}
                    onChange={() => toggleReason(reason)}
                    className="w-5 h-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="font-medium text-gray-900">{reason}</span>
                </label>
              ))}
            </div>
            
            <DialogFooter className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowMarkDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={submitMark}
                disabled={selectedReasons.length === 0 || marking}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                <Flag className="w-4 h-4 mr-2" />
                Mark Product
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Image Lightbox for Zoom */}
      <ImageLightbox
        isOpen={!!lightboxImage}
        onClose={() => {
          setLightboxImage(null);
          setLightboxAlt('');
        }}
        imageUrl={lightboxImage}
        altText={lightboxAlt}
      />
      
      {/* Quick Register Customer Modal */}
      <QuickRegisterCustomer
        isOpen={showQuickRegister}
        onClose={() => setShowQuickRegister(false)}
        onCustomerRegistered={(customer) => {
          // Check if Bangladesh customer
          if (customer.country_code === '880') {
            setBangladeshCustomer(customer);
            setShowBangladeshModal(true);
          } else {
            handleSelectCustomer(customer);
          }
        }}
        token={token}
      />
      
      {/* Bangladesh Export Order Modal - shown when Bangladesh customer is selected */}
      <BangladeshExportOrderModal
        isOpen={showBangladeshModal}
        onClose={() => {
          setShowBangladeshModal(false);
          setBangladeshCustomer(null);
        }}
        onCreated={(data) => {
          setShowBangladeshModal(false);
          setBangladeshCustomer(null);
          onClose(); // Close the main modal too
          toast.success(`Bangladesh export order created: ${data.order_id}`);
        }}
        token={token}
        preSelectedCustomer={bangladeshCustomer}
      />
      
      {/* Duplicate Item Confirmation Dialog */}
      <Dialog open={!!duplicateItemConfirm} onOpenChange={() => setDuplicateItemConfirm(null)}>
        <DialogContent className="max-w-md bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Item Already in Cart
            </DialogTitle>
          </DialogHeader>
          {duplicateItemConfirm && (
            <div className="py-4">
              <p className="text-gray-700 font-medium">
                {duplicateItemConfirm.product?.part_name}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Part No: <span className="font-mono text-red-600">{formatJpWithAlias(duplicateItemConfirm.product || {})}</span>
              </p>
              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-amber-800">
                  This item is already in your cart with <strong>{duplicateItemConfirm.existingQty} PC</strong>.
                </p>
                <p className="text-amber-700 mt-2">
                  Do you want to add <strong>{duplicateItemConfirm.newQty} PC</strong> to the existing quantity?
                </p>
                <p className="text-amber-600 mt-2 text-sm">
                  New total: <strong>{duplicateItemConfirm.existingQty + duplicateItemConfirm.newQty} PC</strong>
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => handleDuplicateConfirm(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleDuplicateConfirm(true)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Yes, Add to Existing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CatalogueOrderModal;
